import React from 'react';
import { motion } from 'framer-motion';
import CountdownTimer from './CountdownTimer';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiUsers, FiClock, FiTarget } = FiIcons;

const HeroSection = ({ onRegisterClick }) => {
  return (
    <section className="relative min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-black/20"></div>
      <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl"></div>
      
      <div className="relative z-10 container mx-auto px-4 py-20">
        <div className="max-w-6xl mx-auto text-center">
          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight"
          >
            Your Business Is{' '}
            <span className="text-red-500">Bleeding Money</span>{' '}
            Silently
          </motion.h1>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-red-400 mb-4"
          >
            — And You Don't Even See Where
          </motion.div>

          {/* Sub-headline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed"
          >
            Discover how top businesses <span className="text-yellow-400 font-semibold">2X to 16X their profits</span> by fixing hidden lead leaks, broken sales systems, low order value, and zero customer retention — using <span className="text-blue-400 font-semibold">ONE automation system</span>.
          </motion.p>

          {/* Authority Line */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-lg text-yellow-400 mb-12 font-medium"
          >
            Based on proven Exponential Growth Checklists used to scale funnels, conversions, AOV, and recurring revenue.
          </motion.p>

          {/* Key Promises */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
          >
            {[
              "Stop losing leads every day",
              "Convert more without hiring sales teams", 
              "Increase order value without more traffic",
              "Build recurring income that runs even when you sleep"
            ].map((promise, index) => (
              <div key={index} className="flex items-start space-x-3 text-left bg-white/5 backdrop-blur-sm p-4 rounded-lg border border-white/10">
                <SafeIcon icon={FiCheckCircle} className="text-green-400 text-xl mt-1 flex-shrink-0" />
                <span className="text-white">{promise}</span>
              </div>
            ))}
          </motion.div>

          {/* Workshop Title */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm border border-blue-400/30 rounded-2xl p-8 mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              10X Profits with <span className="text-yellow-400">FunnelsDone</span>
            </h2>
            <p className="text-xl text-gray-300 mb-6">
              A 4-Day LIVE Workshop for Business Owners to Fix Lead Leaks, Sales Blocks & Revenue Stagnation
            </p>
            
            {/* Countdown Timer */}
            <CountdownTimer />
          </motion.div>

          {/* Primary CTA */}
          <motion.button
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            onClick={onRegisterClick}
            className="bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white text-xl md:text-2xl font-bold py-6 px-12 rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300 mb-8"
          >
            🔴 Register FREE for the 4-Day LIVE Workshop
          </motion.button>

          {/* Trust Elements */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.4 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-400"
          >
            <div className="flex items-center justify-center space-x-2">
              <SafeIcon icon={FiTarget} className="text-green-400" />
              <span>100% Live</span>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <SafeIcon icon={FiClock} className="text-blue-400" />
              <span>No Recordings Will Be Shared</span>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <SafeIcon icon={FiUsers} className="text-yellow-400" />
              <span>Limited Seats</span>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <SafeIcon icon={FiCheckCircle} className="text-purple-400" />
              <span>Action-Focused, Not Theory</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;